import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService } from '@features/dashboard/services/dashboard.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { ActiveCampaign } from '@features/dashboard/models/dashboard.model';
import {
  absoluteTime,
  relativeTime,
} from '@features/dashboard/utils/relative-time';
import { runSegments } from '@features/dashboard/components/latest-activity/latest-activity';

@Component({
  selector: 'app-active-campaigns',
  standalone: true,
  templateUrl: './active-campaigns.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveCampaignsComponent implements OnInit {
  private dashboardService = inject(DashboardService);
  private currentOrgStore = inject(CurrentOrganizationStore);
  private router = inject(Router);

  campaigns = signal<ActiveCampaign[]>([]);
  isLoading = signal(true);
  loadError = signal(false);

  async ngOnInit(): Promise<void> {
    const orgId = this.currentOrgStore.organization()?.id;
    if (!orgId) {
      this.isLoading.set(false);
      return;
    }
    try {
      const { campaigns } = await this.dashboardService.getActiveCampaigns(orgId);
      this.campaigns.set(campaigns);
    } catch (error) {
      console.error('[ActiveCampaigns] Failed to load campaigns:', error);
      this.loadError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Same bar as the activity feed's bulk runs — a campaign's progress is the
   * same three outcomes, counted over its analyses instead of one run.
   */
  segments(c: ActiveCampaign) {
    return runSegments({
      total: c.total,
      dispatched: c.total,
      succeeded: c.succeeded,
      failed: c.failed,
      skipped: 0,
    });
  }

  summary(c: ActiveCampaign): string {
    const open = Math.max(0, c.total - c.succeeded - c.failed);
    return `${c.succeeded} fertig · ${c.failed} Fehler · ${open} offen · ${c.total} gesamt`;
  }

  relativeTime = relativeTime;
  absoluteTime = absoluteTime;

  open(c: ActiveCampaign): void {
    const slug = this.currentOrgStore.organization()?.slug;
    if (slug) {
      this.router.navigate(['/organizations', slug, 'campaigns', c.id]);
    }
  }

  openAll(): void {
    const slug = this.currentOrgStore.organization()?.slug;
    if (slug) {
      this.router.navigate(['/organizations', slug, 'campaigns']);
    }
  }
}
